import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import AuthService from 'services/auth';
import PublicNav from 'layouts/PublicNav';
import PublicFooter from 'layouts/PublicFooter';
import useScrollReveal from 'hooks/useScrollReveal';
import HeroInstall from './HeroInstall';
import HeroScrollFade from './HeroScrollFade';
import HeroOffice from './HeroOffice';
import WorkflowUseCases from 'components/shared/WorkflowUseCases/WorkflowUseCases';
import LoopsSection from './LoopsSection';
import SelfHostedSection from './SelfHostedSection';
import { PillButton } from 'components/atoms';
import { IonToast } from '@ionic/react';
import { warningOutline, closeCircle } from 'ionicons/icons';
import { isRootSiteHost } from 'utils/siteRoots';
import './ConnectPage.css';


// `bare`: the sign-in card alone (route /signin) — for a flow that needs the
// person signed in on the way somewhere (e.g. pricing → checkout), without the
// marketing landing around it. Same component, so sign-in behaves identically.
const ConnectPage = ({ bare = false }) => {
  const { t } = useTranslation();
  const [toast, setToast] = useState({ isOpen: false, message: '', color: 'danger', icon: null });
  const showToast = (message, type = 'error') => {
    setToast({
      isOpen: true,
      message,
      color: type === 'warning' ? 'warning' : 'danger',
      icon: type === 'warning' ? warningOutline : closeCircle
    });
  };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageRef = useScrollReveal();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [passcodeStep, setPasscodeStep] = useState('email');
  const [email, setEmail] = useState('');
  const [passcode, setPasscode] = useState('');
  const [passcodeLoading, setPasscodeLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const verifyingRef = useRef(false);
  const heroLayoutRef = useRef(null);

  // CLI device flow MUST land on the studio subdomain (where the user
  // actually has a session). If a stale CLI sends them to root
  // <root>/connect?deviceCode=..., bounce to studio with the same
  // query string so login + post-login redirect both happen in the
  // correct cookie scope. See CliAuthorizePage for the parallel
  // redirect — together they make the root host a no-op for CLI.
  useEffect(() => {
    const deviceCodeFromUrl = searchParams.get('deviceCode');
    if (!deviceCodeFromUrl) return;
    const host = window.location.hostname;
    if (isRootSiteHost(host)) {
      const studioHost = host.startsWith('www.') ? host.replace('www.', 'studio.') : `studio.${host}`;
      window.location.replace(`https://${studioHost}/connect${window.location.search}`);
    }
  }, [searchParams]);

  // Persist returnTo, plan, and deviceCode from URL params so they survive the OAuth redirect
  useEffect(() => {
    const returnTo = searchParams.get('returnTo');
    const plan = searchParams.get('plan');
    const deviceCode = searchParams.get('deviceCode');

    if (returnTo) {
      sessionStorage.setItem('oauth_return_url', plan ? `${returnTo}?plan=${plan}` : returnTo);
    }
    if (plan) {
      sessionStorage.setItem('selected_plan', plan);
    }
    if (deviceCode) {
      sessionStorage.setItem('cli_device_code', deviceCode);
    }
  }, [searchParams]);

  // Check if user is already authenticated
  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      // CLI authorization: check URL param first, then sessionStorage (survives OAuth redirect)
      const deviceCode = searchParams.get('deviceCode') || sessionStorage.getItem('cli_device_code');
      if (deviceCode) {
        sessionStorage.removeItem('cli_device_code');
        navigate(`/authorize?deviceCode=${deviceCode}`);
        return;
      }
      
      // If there's a returnTo or plan param, redirect there
      const returnTo = searchParams.get('returnTo');
      const plan = searchParams.get('plan');
      
      if (returnTo || plan) {
        if (returnTo) {
          navigate(plan ? `${returnTo}?plan=${plan}` : returnTo);
        } else {
          navigate('/projects');
        }
      }
    }
  }, [navigate, searchParams]);


  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    
    try {
      // This will redirect to Google OAuth, no return expected
      await AuthService.googleSignIn();
      // Note: This line won't execute due to redirect
    } catch (err) {
      showToast('Failed to sign in with Google. Please try again.', 'error');
      console.error('Google sign in error:', err);
      setGoogleLoading(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    setMicrosoftLoading(true);
    
    try {
      // This will redirect to Microsoft OAuth, no return expected
      await AuthService.microsoftSignIn();
      // Note: This line won't execute due to redirect
    } catch (err) {
      showToast('Failed to sign in with Microsoft. Please try again.', 'error');
      console.error('Microsoft sign in error:', err);
      setMicrosoftLoading(false);
    }
  };

  const handleSendPasscode = async (e) => {
    if (e) e.preventDefault();
    const trimmed = (email || '').trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      showToast('Please enter a valid email address.', 'error');
      return;
    }
    // Optimistic jump: go straight to the code step so the user never stares at
    // a cold-start spinner on "Send passcode". The request (which emails the
    // code) runs in the background — we only surface a hard failure as a toast.
    setEmail(trimmed);
    setPasscode('');
    setPasscodeStep('verify');
    AuthService.requestPasscode(trimmed).catch((err) => {
      if (err.retryAfter) {
        showToast(`Please wait ${err.retryAfter}s before requesting a new code`, 'warning');
      } else {
        showToast(err.message || 'Failed to send passcode. Please try again.', 'error');
      }
    });
  };

  const handleVerifyPasscode = async () => {
    if (verifyingRef.current) return;
    const code = (passcode || '').trim();
    if (!code || code.length < 6) {
      showToast('Please enter the 6-digit passcode from your email.', 'error');
      return;
    }
    verifyingRef.current = true;
    setVerifyLoading(true);
    try {
      await AuthService.verifyPasscode(email, code);

      const cliDeviceCode = sessionStorage.getItem('cli_device_code');
      if (cliDeviceCode) {
        sessionStorage.removeItem('cli_device_code');
        navigate(`/authorize?deviceCode=${cliDeviceCode}`);
        return;
      }

      const returnUrl = sessionStorage.getItem('oauth_return_url');
      sessionStorage.removeItem('oauth_return_url');
      sessionStorage.removeItem('selected_plan');
      navigate(returnUrl || '/projects');
    } catch (err) {
      showToast(err.message || 'Invalid or expired passcode. Please try again.', 'error');
    } finally {
      setVerifyLoading(false);
      verifyingRef.current = false;
    }
  };

  return (
    <>
      {/* This page doubles as the marketing landing (/ redirects here for
          anonymous visitors), so it carries the brand title — not "Sign In".
          Google takes the homepage result title + sitelinks from this. */}
      {bare ? (
        <Helmet><title>Sign in — Teamy</title><meta name="robots" content="noindex" /></Helmet>
      ) : (
      <Helmet>
        <title>Teamy — Deploy AI Agents for Your Code & Workflows</title>
        <meta name="description" content="Teamy is the agent-native platform to deploy autonomous AI agents — automated code review, bug fixing, and CI/test triage — plus self-hosted apps. Neutral across GitHub, GitLab, and Slack." />
      </Helmet>
      )}
      <div className={`login-wrapper${bare ? ' login-wrapper--bare' : ''}`} ref={pageRef}>
        {!bare && <HeroScrollFade />}
        {!bare && <HeroOffice layoutRef={heroLayoutRef} />}
        <div className="login-page fade-in">
          <PublicNav transparent />
        <div className="login-split-layout reveal" ref={heroLayoutRef}>
          <div className="login-split-left">
          <div className="login-container">
          <div className="login-card">
            {/* Header */}
            <div className="login-header">
            {bare ? (
              <>
                {/* Says where the sign-in leads: a purchase in progress reads as one. */}
                <h1>Sign in to continue</h1>
                <p className="login-tagline">
                  {(searchParams.get('returnTo') || '').startsWith('/self-hosted/checkout')
                    ? 'Then you’ll go straight to checkout for your self-hosted licence.'
                    : 'Welcome back to Teamy.'}
                </p>
              </>
            ) : (
              <>
                <h1>{t('connect.title')}</h1>
                <p className="login-tagline">The cloud where AI-native companies run their agents, on autopilot.</p>
              </>
            )}
            <p className="login-subtitle">
              {t('connect.subtitle')}
            </p>
          </div>

          {/* Authentication Options */}
          <div>
            <div className="login-buttons">
              {/* Google Sign In */}
              <button
                className="login-button google-button"
                onClick={handleGoogleSignIn}
                disabled={googleLoading || microsoftLoading}
              >
                <>
                  {googleLoading ? (
                    <div className="spinner"></div>
                  ) : (
                    <svg className="button-icon google-icon" viewBox="0 0 24 24" fill="none">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  )}
                  <span className="button-text">{t('connect.connectWithGmail')}</span>
                </>
              </button>

              {/* Microsoft Sign In */}
              <button
                className="login-button microsoft-button"
                onClick={handleMicrosoftSignIn}
                disabled={googleLoading || microsoftLoading}
              >
                <>
                  {microsoftLoading ? (
                    <div className="spinner"></div>
                  ) : (
                    <svg className="button-icon" viewBox="0 0 24 24" fill="none">
                      <path d="M11.4 11.4H0V0h11.4v11.4z" fill="#f25022"/>
                      <path d="M24 11.4H12.6V0H24v11.4z" fill="#00a4ef"/>
                      <path d="M11.4 24H0V12.6h11.4V24z" fill="#ffb900"/>
                      <path d="M24 24H12.6V12.6H24V24z" fill="#7fba00"/>
                    </svg>
                  )}
                  <span className="button-text">{t('connect.connectWithOutlook')}</span>
                </>
              </button>
            </div>
            <div className="login-divider">
              <span className="login-divider-line" />
              <span className="login-divider-text">{t('connect.orDivider')}</span>
              <span className="login-divider-line" />
            </div>

            {passcodeStep === 'email' ? (
              <form className="login-email-section" onSubmit={handleSendPasscode} noValidate={false}>
                <input
                  type="email"
                  required
                  className="login-input"
                  placeholder={t('connect.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={passcodeLoading}
                  autoComplete="email"
                />
                <PillButton
                  type="submit"
                  className="login-button login-button-email"
                  disabled={passcodeLoading}
                >
                  {passcodeLoading ? <div className="spinner" /> : null}
                  <span className="button-text">{t('connect.sendPasscode')}</span>
                </PillButton>
              </form>
            ) : (
              <div className="login-email-section">
                <p className="login-check-email">{t('connect.checkEmail')}</p>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={64}
                  className="login-input login-input-passcode"
                  placeholder={t('connect.passcodePlaceholder')}
                  value={passcode}
                  // Normal logins are a 6-digit numeric code; a reviewer may
                  // paste a long fixed code. Accept alphanumeric and let length
                  // (>= 6) gate submission instead of a hard 6-char cap.
                  onChange={(e) => setPasscode(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
                  disabled={verifyLoading}
                  autoComplete="one-time-code"
                />
                <PillButton
                  type="button"
                  className="login-button login-button-email"
                  onClick={handleVerifyPasscode}
                  disabled={verifyLoading || passcode.length < 6}
                >
                  {verifyLoading ? <div className="spinner" /> : null}
                  <span className="button-text">{t('connect.verifyPasscode')}</span>
                </PillButton>
                <div className="login-resend-row">
                <button
                  type="button"
                  className="login-resend-link"
                  onClick={async () => {
                    setPasscodeLoading(true);
                    try {
                      await AuthService.requestPasscode(email);
                    } catch (err) {
                      if (err.retryAfter) {
                        showToast(`Please wait ${err.retryAfter}s before requesting a new code`, 'warning');
                      } else {
                        showToast(err.message || 'Failed to resend.', 'error');
                      }
                    } finally {
                      setPasscodeLoading(false);
                    }
                  }}
                  disabled={passcodeLoading}
                >
                  {t('connect.resendPasscode')}
                </button>
                <span className="login-resend-sep">·</span>
                <button
                  type="button"
                  className="login-resend-link"
                  onClick={() => { setPasscodeStep('email'); setEmail(''); setPasscode(''); }}
                >
                  {t('connect.useDifferentEmail')}
                </button>
              </div>
              </div>
            )}

            {/* <button
              className="login-button facebook-button"
              onClick={handleFacebookSignIn}
              disabled={googleLoading || microsoftLoading || facebookLoading}
            >
              <>
                {facebookLoading ? (
                  <div className="spinner"></div>
                ) : (
                  <svg className="button-icon facebook-icon" viewBox="0 0 24 24" fill="none">
                    <rect x="0" y="0" width="24" height="24" rx="3" ry="3" fill="#1877F2"/>
                    <path d="M15.12 12.61l.4-2.61h-2.5V8.5c0-.71.35-1.41 1.47-1.41h1.14V4.88s-1.03-.18-2.02-.18c-2.06 0-3.41 1.25-3.41 3.51V10H7.85v2.61h2.35V20h2.89v-7.39h2.03z" fill="white"/>
                  </svg>
                )}
                <span className="button-text">Continue with Facebook</span>
              </>
            </button> */}
          </div>
          {/* Privacy Notice */}
          <div className="login-footer">
            <p>
              {t('connect.termsText')} <a href="/terms" target="_blank" rel="noopener noreferrer">{t('connect.termsOfService')}</a> {t('connect.and')} <a href="/privacy" target="_blank" rel="noopener noreferrer">{t('connect.privacyPolicy')}</a>. 
              {t('connect.linkDescription')}
            </p>
          </div>
          </div>
        </div>
          </div>
          {!bare && (
          <div className="login-split-right">
            {/* The office scene behind the whole hero (HeroOffice) is the
                backdrop; the stage keeps the wall's old footprint so the
                install bar sits where the demo terminal used to. */}
            <div className="login-visual-stack">
              <div className="hero-office-stage" />
              <div className="login-visual-overlay">
                <HeroInstall />
              </div>
            </div>
          </div>
          )}
        </div>

        {!bare && (<>
        <LoopsSection />

        <WorkflowUseCases />

        {/* Self-hosted (run it in your own VPC) — sits above the apps teaser */}
        <SelfHostedSection />

        {/* Apps marketplace teaser */}
        <section className="apps-teaser-section reveal">
          <div className="apps-teaser-grid">
            <div className="apps-teaser-copy">
              <span className="apps-teaser-eyebrow">HOSTED OPEN-SOURCE APPS</span>
              <h2 className="apps-teaser-title">
                Deploy the apps your team already runs — without running them.
              </h2>
              <p className="apps-teaser-subtitle">
                Grafana, Open WebUI, draw.io, Gas Town. One-click hosted instances, private to your
                project, each with an autonomous agent-ops sidecar that handles health checks,
                self-healing, and upgrades.
              </p>
              <div className="apps-teaser-actions">
                <a className="apps-teaser-primary" href="/marketplace/apps">Browse marketplace →</a>
                <a className="apps-teaser-secondary" href="/solutions/apps">Learn more</a>
              </div>
            </div>

            <div className="apps-teaser-stage" aria-hidden="true">
              <div className="apps-teaser-card apps-teaser-card--1">
                <div className="apps-teaser-card-row">
                  <span className="apps-teaser-glyph" style={{ background: '#3d3d3d', color: '#fff' }}>GT</span>
                  <div>
                    <div className="apps-teaser-card-name">Gas Town <span className="apps-teaser-card-ver">v1.1.0</span></div>
                    <div className="apps-teaser-card-desc">Multi-agent workspace</div>
                  </div>
                  <div className="apps-teaser-card-meta">$0.05/hr</div>
                </div>
              </div>
              <div className="apps-teaser-card apps-teaser-card--2">
                <div className="apps-teaser-card-row">
                  <span className="apps-teaser-glyph" style={{ background: '#f46800' }}>G</span>
                  <div>
                    <div className="apps-teaser-card-name">Grafana <span className="apps-teaser-card-ver">v11.4.0</span></div>
                    <div className="apps-teaser-card-desc">Metrics & dashboards</div>
                  </div>
                  <div className="apps-teaser-card-meta">$0.05/hr</div>
                </div>
              </div>
              <div className="apps-teaser-card apps-teaser-card--3">
                <div className="apps-teaser-card-row">
                  <span className="apps-teaser-glyph" style={{ background: '#1a1a1a', color: '#f3f3f1' }}>OI</span>
                  <div>
                    <div className="apps-teaser-card-name">Open WebUI <span className="apps-teaser-card-ver">v0.5.20</span></div>
                    <div className="apps-teaser-card-desc">ChatGPT-style UI for Ollama</div>
                  </div>
                  <div className="apps-teaser-card-meta">$0.25/hr</div>
                </div>
              </div>
              <div className="apps-teaser-card apps-teaser-card--4">
                <div className="apps-teaser-card-row">
                  <span className="apps-teaser-glyph" style={{ background: '#f8a925' }}>◢</span>
                  <div>
                    <div className="apps-teaser-card-name">draw.io <span className="apps-teaser-card-ver">v30.0.2</span></div>
                    <div className="apps-teaser-card-desc">Diagrams & flowcharts</div>
                  </div>
                  <div className="apps-teaser-card-meta">$0.05/hr</div>
                </div>
              </div>
              <div className="apps-teaser-pulse">
                <span className="apps-teaser-pulse-dot" />
                <span>3 instances running · self-healing on</span>
              </div>
            </div>
          </div>
        </section>
        </>)}

        </div>
        {!bare && <PublicFooter />}
      </div>
      <IonToast
        isOpen={toast.isOpen}
        onDidDismiss={() => setToast(prev => ({ ...prev, isOpen: false }))}
        message={toast.message}
        duration={4000}
        icon={toast.icon}
        position="bottom"
        cssClass="custom-toast"
      />
    </>
  );
};

export default ConnectPage;
